import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { TreeForm } from "../components/forms/tree_form";
import { useHeaderTags } from "../components/custom_hook";
import { SummaryFolder } from "../components/summary_folder";
import { SummaryGraph } from "../components/summary_graph";
import { SummaryTape } from "../components/summary_tape";
import { PrefixClasz } from "../components/prefix_class";

export const TreeFlow = (props) => {
  const [prefixes, setPrefixes] = useState();
  const [showHelp, setShowHelp] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refresh, setRefresh] = useState(true);
  const [winWidth, setWinWidth] = useState(1000);
  const [parent, setParent] = useState();

  const codeRef = useRef();

  useHeaderTags(props);
  const fromNet = searchParams.get("from")
  const toNet = searchParams.get("to")

  const followSub = (e) => {
    let tar = e.currentTarget.textContent.replace("/", "_");
    window.location.href = "/act/app/subnet/" + tar;
  }

  const getFormat = (tag) => {
    switch (tag) {
      case 'f':
        return 'folder';
      case 'x':
        return 'text-contrast';
      default:
        return ''
    }
  }

  const updatePrefixes = (p) => {
    setParent(null);
    setPrefixes(p);
  }

  const setFocusNode = (node) => {
    if (node == null) {
      setParent(null);
      return;
    }
    let nets = prefixes.nodes.map(p => {
      return new PrefixClasz(p);
    });
    let result = [];
    if (nets[0].is4())
      nets.forEach(n => {
        result = n.findPath4(node, result);
      });
    else
      nets.forEach(n => {
        result = n.findPath6(node, result);
      });
    result.reverse();
    setParent(result);
  }

  useEffect(() => {
    if (refresh && codeRef.current) {
      setRefresh(false);
      let panel = codeRef.current.parentNode.parentNode.clientWidth - 58;
      let codeNode = codeRef.current.parentNode.getElementsByTagName("code")[0].getBoundingClientRect().width;
      setWinWidth(panel - codeNode);
    }
  }, [refresh]);


  return (
    <>
      <Container>
        <TreeForm
          setPrefixes={updatePrefixes}
          showHelp={setShowHelp}
          doRefresh={setRefresh}
          prePopulate={fromNet ? { fromNet, toNet } : undefined}
        ></TreeForm>
        {showHelp &&
          <div className="docs paper">
            <h3>Description</h3>
            <p>Our subnet tree tool analyzes lists of IP subnets and organizes them into a hierarchical binary tree structure. Ideal for network administrators and engineers examining address plans to identify gaps, overlaps, and potential summary routes in IPv4 and IPv6 networks.</p>
            <p>This powerful subnet calculator helps with:</p>
            <ul>
              <li>Identifying potential summary routes for efficient routing</li>
              <li>Discovering the subnet hierarchy in a list of prefixes</li>
              <li>Finding overlapping subnets in your network</li>
              <li>Locating summaries with missing subnets</li>
              <li>Filtering subnets through a prefix-filter element</li>
              <li>Determining if two IP addresses are on the same subnet</li>
            </ul>
            <br /><h3>Usage</h3>
            <p>Learn how to use our subnet tree calculator: Enter a list of subnets, IP addresses, or ranges in the input field above and submit. The tool will analyze and organize them into a hierarchical binary tree structure, helping you identify gaps, overlaps, and summary routes in your network address plan.</p>
            
            <h4>Input Formats for Subnet Tree Tool</h4>
            <ul>
              <li><strong>Subnets:</strong> Enter CIDR notation like <code>192.168.1.0/24</code> or <code>2001:db8::/32</code> for IPv4 and IPv6 subnet analysis.</li>
              <li><strong>IP Addresses:</strong> Enter individual IPs like <code>192.168.1.1</code> to find the smallest common subnet covering them.</li>
              <li><strong>Ranges:</strong> Use a hyphen between two addresses or subnets, e.g., <code>10.20.30.40/20 - 10.21.22.23</code>, for range-based subnet calculations.</li>
            </ul>
            
            <h4>Interpreting Subnet Tree Output</h4>
            <p>The output displays a visual subnet tree with folders and tapes representing the IP subnet hierarchy.</p>
            <ul>
              <li><strong>Folders:</strong> Represent summary blocks or aggregated subnets. Outlined folders are auto-generated to cover gaps or missing subnets in your address space.</li>
              <li><strong>Tapes:</strong> Visualize the size and position of subnets within the hierarchy, showing how they fit together in the binary tree.</li>
            </ul>
            <p>The graph helps you spot overlaps, gaps, and potential summary routes. Click on folders or tapes to focus or navigate the subnet hierarchy.</p>
            
            <h4>Subnet Tree Options</h4>
            <ul>
              <li><strong>Include Missing Subnets:</strong> Adds auto-generated folders to cover unseen prefixes, filling in the address space for complete network planning.</li>
              <li><strong>Fixed Mask Size:</strong> Set a specific mask size for folders (e.g., /24); set to 0 to disable folder creation and show only natural overlaps in your subnet list.</li>
              <li><strong>Prefix Filter:</strong> Filter the input list to show only subnets within a specified prefix range, useful for targeted network analysis.</li>
            </ul>
            
            <h4>Subnet Tree Examples</h4>
            <p>Try these practical examples by pasting them into the input field to see the subnet tree calculator in action:</p>
            <div className="tab">
              <pre>
                <code>
                  10.33.99.0/27<br />
                  192.168.7.0/24<br />
                  10.64.110.0/24<br />
                  172.16.32.0/22<br />
                  172.16.0.0/18<br />
                </code>
              </pre>
            </div>
            <p>This creates summary blocks organized around bit boundaries, useful for studying route tables or network address plans.</p>
            
            <pre>
              <code>
                2000:1000:500:24c::/64<br />
                2000:1000:500:251::/64<br />
                2000:1000:500:250::/62<br />
                2000:1000:520:250::/64
              </code>
            </pre>
            <p>IPv6 subnet example showing subnet organization in IPv6 networks.</p>
            
            <pre>
              <code>
                192.168.1.1<br />
                192.168.1.14<br />
              </code>
            </pre>
            <p>Finds the common subnet for these IP addresses using our subnet calculator.</p>
            
            <h4>Range Example for Subnet Analysis</h4>
            <p>Entering <code>10.20.30.40/20 - 10.21.22.23</code> shows the smallest subnet covering both (10.20.0.0/15), with the graph illustrating their positions in the subnet hierarchy.</p>
            <img style={{'width': '65%'}} src={process.env.REACT_APP_HOST+"/img/tree_ss_1.png"}></img>
            <br/>This tells us that 10.20.0.0/15 is the smallest subnet that covers both the /20 and the 10.21.22.23 IP address. The tool has determined that 10.21.22.16/29 is 
            the smallest subnet that could hold the IP address.  It has also ignored the hostbits that we included in the /20 leaving 10.20.16.0/20 as the 
            correct subnet.  The graph also represents the respective size and location of the two smaller subnets under the common /15.
            <br/><h5>Notes:</h5>
            <p>
              Auto generated common subnets folders are shown <span className="folder">outlined</span>.
            </p>  
          </div>}
      </Container>
      {!showHelp && (
        <Container>
          <Row>
            <Col id="subs">
              <div>
                <div>&nbsp;</div>
                <div className="text-dark">
                  Subnets and Folders
                </div>
                <pre className="half-panel inline">
                  <code ref={codeRef}>
                    {prefixes &&
                      prefixes.nodes.map((subnet, id) => {
                        return (
                          <SummaryFolder setRefresh={setRefresh} canvasRef={codeRef} key={id} folder={subnet} />
                        );
                      })}
                  </code>
                  <svg width={winWidth}>
                    {prefixes && codeRef.current &&
                      prefixes.nodes.map((subnet, id) => {
                        return (
                          <SummaryTape parents={{ parent: parent, setFocusNode: setFocusNode }} winWidth={winWidth - 25} refresh={refresh} key={id} folder={subnet} root={true} box={{ x: 0.0, w: 1.0 }} parentY={0} />
                        );
                      })}
                  </svg>
                </pre>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};
